import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { PropertyValuationData } from "../../types/valuation";
import { TenantConfig } from "../../types/tenant";
import { formatCurrency } from "../../utils/formatters";

interface PropertyReportProps {
  propertyData: PropertyValuationData;
  tenant: TenantConfig;
}

const styles = StyleSheet.create({
  page: {
    padding: 25,
    fontFamily: "Helvetica",
    backgroundColor: "#FFFFFF",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    backgroundColor: "#F9FAFB",
    margin: -25,
    marginBottom: 15,
    padding: 25,
    paddingBottom: 12,
  },
  headerLeft: {
    flex: 1,
  },
  title: {
    fontSize: 20,
    fontFamily: "Helvetica-Bold",
    marginBottom: 8,
    color: "#111827",
  },
  address: {
    fontSize: 14,
    color: "#111827",
  },
  location: {
    fontSize: 12,
    color: "#6B7280",
    marginTop: 2,
  },
  mainContent: {
    flexDirection: "row",
    gap: 15,
  },
  leftColumn: {
    flex: 1,
  },
  rightColumn: {
    flex: 1,
  },
  valueBox: {
    backgroundColor: "#FFFFFF",
    borderRadius: 4,
    padding: 12,
    marginBottom: 15,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  valueLabel: {
    fontSize: 12,
    color: "#6B7280",
    marginBottom: 2,
  },
  value: {
    fontSize: 24,
    fontFamily: "Helvetica-Bold",
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: "Helvetica-Bold",
    marginBottom: 8,
    color: "#111827",
  },
  statsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  statBox: {
    width: "47%",
    padding: 10,
    backgroundColor: "#F9FAFB",
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  statLabel: {
    fontSize: 10,
    color: "#6B7280",
    marginBottom: 2,
  },
  statValue: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    color: "#111827",
  },
  compsTable: {
    width: "100%",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    padding: 8,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    padding: 8,
  },
  tableCell: {
    flex: 1,
    fontSize: 9,
    color: "#374151",
  },
  tableCellHeader: {
    flex: 1,
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: "#111827",
  },
  tableCellLarge: {
    flex: 2,
  },
  rentalSection: {
    flexDirection: "row",
    gap: 8,
  },
  rentalBox: {
    flex: 1,
    padding: 10,
    backgroundColor: "#F9FAFB",
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  rentalTitle: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    color: "#111827",
    marginBottom: 4,
  },
  rentalValue: {
    fontSize: 16,
    fontFamily: "Helvetica-Bold",
    color: "#111827",
    marginBottom: 2,
  },
  rentalDetail: {
    fontSize: 10,
    color: "#6B7280",
  },
  footer: {
    position: "absolute",
    bottom: 25,
    left: 25,
    right: 25,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    paddingTop: 12,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  footerText: {
    fontSize: 8,
    color: "#6B7280",
  },
  disclaimer: {
    fontSize: 7,
    color: "#9CA3AF",
    textAlign: "center",
    marginTop: 4,
  },
});

const ReportContent: React.FC<PropertyReportProps> = ({
  propertyData,
  tenant,
}) => {
  const annualRent = propertyData.demographics?.suggestedRent
    ? propertyData.demographics.suggestedRent * 12
    : 0;
  const capRate = annualRent
    ? ((annualRent / propertyData.estimatedValue) * 100).toFixed(1)
    : null;

  return (
    <Page size="A4" style={styles.page}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Text style={styles.title}>Property Value Report</Text>
          {propertyData.propertyInfo?.address && (
            <>
              <Text style={styles.address}>
                {propertyData.propertyInfo.address.address}
              </Text>
              <Text style={styles.location}>
                {propertyData.propertyInfo.address.city},{" "}
                {propertyData.propertyInfo.address.state}{" "}
                {propertyData.propertyInfo.address.zip}
              </Text>
            </>
          )}
        </View>
      </View>

      <View style={styles.mainContent}>
        {/* Left Column */}
        <View style={styles.leftColumn}>
          {/* Value Box */}
          <View style={styles.valueBox}>
            <Text style={styles.valueLabel}>Current Value</Text>
            <Text
              style={[styles.value, { color: tenant.branding.colors.primary }]}
            >
              {formatCurrency(propertyData.estimatedValue)}
            </Text>
            {propertyData.lastSalePrice && (
              <Text style={styles.valueLabel}>
                {(
                  ((propertyData.estimatedValue - propertyData.lastSalePrice) /
                    propertyData.lastSalePrice) *
                  100
                ).toFixed(1)}
                % change since last sale
              </Text>
            )}
          </View>

          {/* Property Details */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Property Details</Text>
            <View style={styles.statsGrid}>
              <View style={styles.statBox}>
                <Text style={styles.statLabel}>Bedrooms</Text>
                <Text style={styles.statValue}>
                  {propertyData.propertyInfo?.bedrooms || "N/A"}
                </Text>
              </View>
              <View style={styles.statBox}>
                <Text style={styles.statLabel}>Bathrooms</Text>
                <Text style={styles.statValue}>
                  {propertyData.propertyInfo?.bathrooms || "N/A"}
                </Text>
              </View>
              <View style={styles.statBox}>
                <Text style={styles.statLabel}>Square Feet</Text>
                <Text style={styles.statValue}>
                  {propertyData.propertyInfo?.livingSquareFeet?.toLocaleString() ||
                    "N/A"}
                </Text>
              </View>
              <View style={styles.statBox}>
                <Text style={styles.statLabel}>Year Built</Text>
                <Text style={styles.statValue}>
                  {propertyData.propertyInfo?.yearBuilt || "N/A"}
                </Text>
              </View>
            </View>
          </View>

          {/* Rental Analysis */}
          {propertyData.demographics?.suggestedRent && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Rental Analysis</Text>
              <View style={styles.rentalSection}>
                <View style={styles.rentalBox}>
                  <Text style={styles.rentalTitle}>Monthly Rent</Text>
                  <Text style={styles.rentalValue}>
                    {formatCurrency(propertyData.demographics.suggestedRent)}
                  </Text>
                </View>
                <View style={styles.rentalBox}>
                  <Text style={styles.rentalTitle}>Cap Rate</Text>
                  <Text style={styles.rentalValue}>{capRate}%</Text>
                </View>
              </View>
            </View>
          )}
        </View>

        {/* Right Column */}
        <View style={styles.rightColumn}>
          {/* Additional Stats */}
          <View style={styles.section}>
            <View style={styles.statsGrid}>
              <View style={styles.statBox}>
                <Text style={styles.statLabel}>Price/Sqft</Text>
                <Text style={styles.statValue}>
                  {propertyData.propertyInfo?.livingSquareFeet
                    ? formatCurrency(
                        propertyData.estimatedValue /
                          propertyData.propertyInfo.livingSquareFeet
                      )
                    : "N/A"}
                </Text>
              </View>
              <View style={styles.statBox}>
                <Text style={styles.statLabel}>Property Type</Text>
                <Text style={styles.statValue}>
                  {propertyData.propertyType || "N/A"}
                </Text>
              </View>
              <View style={styles.statBox}>
                <Text style={styles.statLabel}>Lot Size</Text>
                <Text style={styles.statValue}>
                  {propertyData.propertyInfo?.lotSquareFeet?.toLocaleString() ||
                    "N/A"}
                </Text>
              </View>
              <View style={styles.statBox}>
                <Text style={styles.statLabel}>Occupancy</Text>
                <Text style={styles.statValue}>
                  {propertyData.ownerOccupied ? "Owner" : "Tenant"}
                </Text>
              </View>
            </View>
          </View>

          {/* Comparable Properties */}
          {propertyData.comps && propertyData.comps.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Comparable Properties</Text>
              <View style={styles.compsTable}>
                <View style={styles.tableHeader}>
                  <Text style={[styles.tableCellHeader, styles.tableCellLarge]}>
                    Address
                  </Text>
                  <Text style={styles.tableCellHeader}>Price</Text>
                  <Text style={styles.tableCellHeader}>Sqft</Text>
                  <Text style={styles.tableCellHeader}>Beds/Baths</Text>
                </View>
                {propertyData.comps.slice(0, 5).map((comp, index) => (
                  <View key={index} style={styles.tableRow}>
                    <Text style={[styles.tableCell, styles.tableCellLarge]}>
                      {comp.address?.address?.split(",")[0] || "N/A"}
                    </Text>
                    <Text style={styles.tableCell}>
                      {formatCurrency(
                        comp.mlsSoldPrice || comp.estimatedValue || 0
                      )}
                    </Text>
                    <Text style={styles.tableCell}>
                      {comp.squareFeet?.toLocaleString() || "N/A"}
                    </Text>
                    <Text style={styles.tableCell}>
                      {comp.bedrooms || "N/A"}/{comp.bathrooms || "N/A"}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          )}
        </View>
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <Text style={styles.footerText}>{tenant.name}</Text>
        <Text style={styles.footerText}>{new Date().toLocaleDateString()}</Text>
      </View>
      <Text style={styles.disclaimer}>
        Values and analysis are estimates based on available data. Actual values
        may vary.
      </Text>
    </Page>
  );
};

const PropertyReport: React.FC<PropertyReportProps> = (props) => (
  <Document>
    <ReportContent {...props} />
  </Document>
);

export default PropertyReport;
